import React, { useRef, useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { useSiteContent } from '../hooks/useSiteContent';
import { smoothScrollToHash } from '../lib/smoothScroll';

/**
 * Memberships — unified "Membership Command Center" layout.
 *
 * Previously: 3 large stacked pricing cards + a separate enrollment CTA
 * panel further down the page. Users had to scroll back-and-forth between
 * "compare plans" and "actually enroll", which hurt conversion.
 *
 * Now: 3 compact tier cards sit ABOVE one dominant center enrollment
 * card, all inside the same section. Tapping a tier card updates the
 * helper line inside the center card ("On the next page, choose ...")
 * and briefly flashes the official-enrollment button to draw the eye —
 * NO external open, NO modal, NO drawer, NO route change. The single
 * canonical hand-off to MyMemberAccount lives only on the center CTA
 * button.
 *
 * Responsive:
 *   • Desktop / Laptop (lg+): 3 mini cards in a 3-col row, center card
 *     below at max-w-4xl. Both are in viewport on standard laptop
 *     heights.
 *   • Tablet (md): same 3-col mini-row, center card stacks below.
 *   • Mobile: 3 mini cards stack 1-column, center card last.
 *
 * Performance:
 *   • Pure CSS animations (existing .enroll-cta-pulse / .enroll-cta-button-flash).
 *   • One small useState + useRef. No scroll listeners, no observers, no fetch.
 */

// Per-plan helper text shown inside the central enrollment card when a
// mini card is tapped. Maps to the EXACT label the user must pick on
// MyMemberAccount's "Select Membership" page (MMA does not honour
// `?plan=` deep links — same page for all 3 — so we tell users what to
// click next here).
const PLAN_HELPER = {
  1: '1-Year Individual Membership',
  2: '1-Year Family Membership',
  3: '1-Year Paid in Full Membership - Family',
};

// Compact mini-card copy. Decoupled from `seed_data.memberships` so the
// new tighter section copy ("Monthly membership for one member.") can
// evolve without touching the canonical pricing-rules in the DB.
const MINI_CARDS = [
  {
    id: 1, name: '1-Year Individual', price: '$49', priceSuffix: '/mo',
    blurb: 'Monthly membership for one member.', cta: 'Choose Individual',
  },
  {
    id: 2, name: 'Family Plan', price: '$49', priceSuffix: '/mo+',
    blurb: 'First member starts at $49/month.', cta: 'Choose Family',
    highlight: true, // visually emphasised tier (matches existing highlight pattern)
  },
  {
    id: 3, name: 'Paid in Full', price: '$490', priceSuffix: 'one-time',
    blurb: 'One-time annual payment option.', cta: 'Choose Paid in Full',
  },
];

const ENROLLMENT_URL =
  'https://www.mymemberaccount.com/member-enrollment/2440/group/2604?view=offer&plan=1-year-individual';

export default function Memberships() {
  // useSiteContent kept to preserve API hydration semantics — the
  // section is still gated by content load, so the rest of the page's
  // skeleton-show timing stays unchanged.
  useSiteContent();

  // selectedId: which mini card was last tapped (drives the helper-
  // text inside the center card + the transient button flash).
  const [selectedId, setSelectedId] = useState(null);

  // Transient flash key — incrementing it remounts the className with
  // a fresh keyframe start, so consecutive taps always re-trigger the
  // animation even if the same plan was selected.
  const [flashKey, setFlashKey] = useState(0);
  const flashTimerRef = useRef(null);

  // ----- Cursor-following 3D tilt + glare (desktop only) -----
  // Pure CSS transform driven by inline style updates. No state, no
  // re-renders, no scroll listeners. Disabled at the CSS layer for
  // touch devices and `prefers-reduced-motion` via media queries.
  const handle3DTilt = (e) => {
    const card = e.currentTarget;
    // Skip on touch / coarse pointers (matches the CSS media query).
    if (window.matchMedia && window.matchMedia('(hover: none), (pointer: coarse)').matches) return;
    const rect = card.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const rotateY = (x - 0.5) * 14;   // -7deg .. 7deg
    const rotateX = (0.5 - y) * 10;   // -5deg .. 5deg
    card.style.setProperty('--tilt-rx', `${rotateX}deg`);
    card.style.setProperty('--tilt-ry', `${rotateY}deg`);
    card.style.setProperty('--mx', `${x * 100}%`);
    card.style.setProperty('--my', `${y * 100}%`);
  };
  const reset3DTilt = (e) => {
    const card = e.currentTarget;
    card.style.setProperty('--tilt-rx', '0deg');
    card.style.setProperty('--tilt-ry', '0deg');
  };

  const handleMiniSelect = (id) => {
    setSelectedId(id);
    setFlashKey((k) => k + 1);
    try {
      sessionStorage.setItem('selectedPlan', JSON.stringify({ id, name: PLAN_HELPER[id] }));
      window.dispatchEvent(
        new CustomEvent('selectedPlanChanged', { detail: { id, name: PLAN_HELPER[id] } }),
      );
    } catch (_) { /* sessionStorage may be unavailable in private tabs */ }
    // If user is somehow above the section (e.g. mid-scroll), bring
    // the enrollment card into view. On in-view taps this is a no-op
    // since smoothScroll exits early when the target is already
    // visible. history:'none' keeps the URL clean.
    smoothScrollToHash('#membership-enrollment', { history: 'none' });
    // Clear flash class after the animation completes so subsequent
    // taps re-trigger cleanly.
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => { setFlashKey((k) => k); }, 1400);
  };

  return (
    <section
      id="memberships"
      data-testid="memberships-section"
      className="relative pt-16 pb-16 sm:pt-24 sm:pb-20 lg:pt-32 lg:pb-24 bg-zinc-950 overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg opacity-20" />
      <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-red-600/10 blur-3xl rounded-full" />
      <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-red-600/10 blur-3xl rounded-full" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Section header — unchanged eyebrow + headline copy */}
        <div className="text-center mb-10 sm:mb-14 reveal">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-[2px] w-10 bg-red-600" />
            <span className="text-xs tracking-[0.4em] text-red-500 font-semibold">MEMBERSHIPS</span>
            <div className="h-[2px] w-10 bg-red-600" />
          </div>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.9] tracking-tight">
            CHOOSE YOUR <span className="text-gradient-red">PATH</span>
          </h2>
        </div>

        {/* === UNIFIED MEMBERSHIP COMMAND CENTER ===
            One bordered, glowing container holding BOTH columns so the
            section reads as a single "command center" — never two
            disconnected blocks.
            Desktop / laptop (lg+): two-column layout INSIDE the wrapper.
              • LEFT (2/5 cols): vertical stack of 3 compact tier cards
              • RIGHT (3/5 cols): dominant enrollment card
            Tablet / mobile (<lg): single-column stack — enrollment
            card first for instant CTA visibility, then 3 plan-selector
            cards below for context. Mobile users tap one of the small
            cards which updates the helper line inside the now-above
            enrollment card AND flashes the official button (or smooth-
            scrolls to it if it's already above viewport). */}
        <div
          id="membership-enrollment"
          data-testid="membership-command-center"
          className="relative max-w-6xl mx-auto rounded-3xl border border-red-600/40 bg-gradient-to-br from-zinc-950 via-black to-zinc-950 p-4 sm:p-6 lg:p-8 shadow-[0_30px_120px_-20px_rgba(239,43,43,0.45),0_0_0_1px_rgba(239,43,43,0.12)] overflow-hidden"
        >
          {/* unified inner glow accents (purely decorative) */}
          <div
            aria-hidden
            className="pointer-events-none absolute -top-1/2 -right-1/3 w-2/3 h-full bg-red-600/10 blur-3xl rounded-full"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-1/2 -left-1/3 w-2/3 h-full bg-red-600/10 blur-3xl rounded-full"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 h-[3px] w-1/3 bg-gradient-to-r from-transparent via-red-500 to-transparent"
          />

          <div className="relative grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6 lg:gap-8 items-stretch">
          {/* ---------- LEFT COLUMN: PLAN SELECTORS ---------- */}
          <div
            data-testid="memberships-mini-row"
            className="order-2 lg:order-1 lg:col-span-2 flex flex-col gap-3 sm:gap-4"
          >
            {MINI_CARDS.map((p) => {
              const isSelected = selectedId === p.id;
              const isHighlight = p.highlight;
              return (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => handleMiniSelect(p.id)}
                  onMouseMove={handle3DTilt}
                  onMouseLeave={reset3DTilt}
                  data-cursor="hover"
                  data-testid={`pricing-card-${p.id}`}
                  aria-pressed={isSelected}
                  className={`tilt-3d mini-tier-card group relative text-left rounded-2xl p-4 sm:p-5 flex flex-col gap-1.5 sm:gap-2 border transition-all
                    ${isHighlight
                      ? 'bg-gradient-to-br from-red-600/20 via-red-700/10 to-black border-red-500/55'
                      : 'bg-black/70 border-zinc-800'}
                    ${isSelected ? 'mini-tier-selected ring-2 ring-red-500/70' : 'hover:border-red-600/55'}
                  `}
                >
                  {isHighlight && (
                    <span
                      aria-hidden
                      data-testid="pricing-card-popular-ribbon"
                      className="absolute -top-2.5 right-3 sm:right-4 z-[2] pointer-events-none rounded-full bg-red-600 text-white text-[9px] sm:text-[10px] tracking-[0.22em] font-bold px-2.5 py-1 shadow-[0_6px_18px_rgba(239,43,43,0.55)]"
                      style={{ transform: 'translateZ(40px)' }}
                    >
                      MOST POPULAR
                    </span>
                  )}

                  {/* One-shot red particle burst on selection. Keyed by
                      flashKey so each click remounts → CSS animation
                      restarts from frame 0. Auto-completes in ~800ms.
                      Disabled at the CSS layer for prefers-reduced-
                      motion users. */}
                  {isSelected && flashKey > 0 && (
                    <span
                      key={`burst-${flashKey}`}
                      aria-hidden
                      data-testid={`pricing-card-burst-${p.id}`}
                      className="card-burst pointer-events-none absolute inset-0 overflow-visible z-[3]"
                    >
                      {[0, 1, 2, 3, 4, 5].map((i) => (
                        <span key={i} className={`card-burst-particle card-burst-particle-${i}`} />
                      ))}
                    </span>
                  )}
                  <div className="flex items-center justify-between">
                    <span className={`text-[10px] sm:text-[11px] tracking-[0.28em] sm:tracking-[0.32em] font-bold ${isHighlight ? 'text-red-300' : 'text-zinc-400'}`}>
                      {p.name.toUpperCase()}
                    </span>
                    <span
                      aria-hidden
                      className={`flex items-center justify-center h-5 w-5 rounded-full border transition-opacity
                        ${isSelected
                          ? 'bg-red-600 border-red-400 opacity-100'
                          : 'border-zinc-700 opacity-0 group-hover:opacity-60'}`}
                    >
                      <Check size={12} strokeWidth={3} color="#fff" />
                    </span>
                  </div>

                  <div className="flex items-baseline gap-1.5 mt-0.5">
                    <span className="font-display text-2xl sm:text-3xl text-white leading-none tabular">
                      {p.price}
                    </span>
                    <span className={`text-[10.5px] sm:text-[11px] tracking-[0.18em] font-semibold ${isHighlight ? 'text-red-200' : 'text-zinc-500'}`}>
                      {p.priceSuffix}
                    </span>
                  </div>

                  <p className="text-[12px] sm:text-[13px] leading-snug text-zinc-300 mt-0.5">
                    {p.blurb}
                  </p>

                  <span
                    data-testid={`pricing-cta-${p.id}`}
                    className={`inline-flex items-center justify-center gap-2 mt-2 px-3 py-2 rounded-md text-[10px] sm:text-[10.5px] tracking-[0.22em] sm:tracking-[0.24em] font-bold transition-colors
                      ${isHighlight
                        ? 'bg-red-600 text-white group-hover:bg-red-500'
                        : 'bg-zinc-900 text-zinc-200 group-hover:bg-red-600 group-hover:text-white'}`}
                  >
                    {p.cta.toUpperCase()}
                    <ArrowRight className="w-3 h-3" strokeWidth={3} />
                  </span>
                </button>
              );
            })}
          </div>

          {/* ---------- RIGHT COLUMN: DOMINANT ENROLLMENT CARD ---------- */}
          <div
            data-testid="memberships-official-cta-panel"
            onMouseMove={handle3DTilt}
            onMouseLeave={reset3DTilt}
            className="enroll-cta-border-glow tilt-3d tilt-3d-subtle enroll-cta-pulse order-1 lg:order-2 lg:col-span-3 relative overflow-hidden rounded-2xl border border-red-600/50 bg-zinc-950/95 px-6 py-10 sm:px-10 sm:py-14 lg:px-14 lg:py-16 shadow-[0_30px_90px_rgba(239,43,43,0.25),0_0_0_1px_rgba(239,43,43,0.1)] flex flex-col justify-center"
          >
            {/* desktop-only spotlight halo behind the card */}
            <div aria-hidden className="enroll-spotlight" />
            <div
              aria-hidden
              className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-red-500 to-transparent"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -inset-32 opacity-40"
              style={{
                background:
                  'radial-gradient(circle at 50% 0%, rgba(239,43,43,0.35) 0%, transparent 60%)',
              }}
            />

            <div className="relative z-[1] flex flex-col items-center text-center">
              <div className="inline-flex items-center gap-2 text-[10px] sm:text-[11px] tracking-[0.4em] text-red-400 font-bold mb-4">
                <span className="h-px w-6 bg-red-500" aria-hidden />
                OFFICIAL ENROLLMENT
                <span className="h-px w-6 bg-red-500" aria-hidden />
              </div>

              <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight max-w-2xl">
                Ready to Lock In Your{' '}
                <span className="text-red-500">Membership?</span>
              </h3>

              <p className="mt-5 text-sm sm:text-base text-zinc-300 leading-relaxed max-w-xl">
                Choose your plan on the official FitnessPro Galt enrollment
                page and complete your membership securely.
              </p>

              <a
                key={`flash-${flashKey}`}
                href={ENROLLMENT_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="memberships-official-cta-button"
                className={`btn-shimmer enroll-cta-button ${flashKey > 0 ? 'enroll-cta-button-flash' : ''} relative inline-flex items-center justify-center gap-3 mt-8 px-8 sm:px-10 py-4 sm:py-5 rounded-md bg-red-600 hover:bg-red-500 text-white text-xs sm:text-sm tracking-[0.28em] font-bold shadow-[0_14px_46px_rgba(239,43,43,0.55)] transition-colors`}
              >
                <span>OPEN OFFICIAL MEMBERSHIP PAGE</span>
                <ArrowRight className="enroll-cta-arrow w-4 h-4" strokeWidth={3} />
              </a>

              <p
                data-testid="memberships-helper-text"
                className="mt-5 text-xs text-zinc-500 max-w-md leading-relaxed min-h-[2.5rem]"
              >
                {selectedId
                  ? (<>On the next page, choose <span className="text-white font-semibold">&ldquo;{PLAN_HELPER[selectedId]}&rdquo;</span>.</>)
                  : (<>You&rsquo;ll see Individual, Family, and Paid-in-Full options on the next page.</>)}
              </p>
            </div>
          </div>
          </div>
        </div>
      </div>
    </section>
  );
}
